import React, { FC, useRef } from 'react'
import {
  Button,
  ButtonType,
  ComparisonIncludesList,
  ComparisonItemProps,
  Div,
  Flex,
  Spacer,
  Transition,
  UpsaleDialog,
} from '@sendoutcards/quantum-design-ui'
import { useActions, useQueries, useSelector, useState } from 'src/hooks'
import InnerPaymentSheet, {
  InnerPaymentSheetHandle,
  UpsaleResponseData,
} from 'src/onboarding/components/InnerPaymentSheet'
import { PlansDescription } from 'src/marketing/plans/types'
import { getPlansDescriptions } from 'src/legacy_graphql'
import PlanUpsaleInnerPaymentSheet from 'src/plan_upsale/PlanUpsaleInnerPaymentSheet'
import { usePlanAddons } from 'src/hooks/usePlanAddons'
import { UpsaleOptionType } from '@sendoutcards/quantum-design-ui/dist/src/portfolios/soc/upsaleDialog/upsaleOptions'

const options: UpsaleOptionType[] = [
  {
    title: '$20.00/10 Card Challenge',
    subtitle:
      'Send 10 Heartfelt Prompting Cards anywhere in the world for a one-time cost of $20, stamps included. Cards good for 20 days.',
    optionId: '122',
  },
  {
    title: '$37.00/Month Heartfelt',
    subtitle: '20 Heartfelt Prompting Cards - stamps included!',
    featureCaption: 'Top Seller',
    optionId: '6',
  },
  {
    title: '$97.00/Month Heartfelt Plus',
    subtitle: 'Up to 5 Heartfelt Prompting Cards per day - stamps included!',
    featureCaption: 'Most Popular',
    optionId: '128',
  },
  {
    title: '$147.00/Month Heartfelt Plus Gifts',
    subtitle:
      'Up to 5 Heartfelt Prompting Cards per day and 5 Best Seller Gifts per month - stamps and shipping included!',
    featureCaption: 'Incredible Value',
    optionId: '129',
  },
]

const comparisonItems: { [key: string]: ComparisonItemProps[] } = {
  '122': [
    {
      title: '10 Card Credits can be Applied to:',
      description: 'Heartfelt Promptings Cards - stamps included',
      isIncluded: true,
    },
    {
      title: 'Contact Manager',
      description:
        'Store your contacts information - such as their address, birthdays, special occasions, reminders, include notes, and get a history of every card sent.',
      isIncluded: true,
    },
    {
      title: 'Premium Storage',
      description:
        'Premium storage allows you to save unlimited cards, pictures, campaigns, and contacts.',
      isIncluded: true,
    },
  ],
  '6': [
    {
      title: '20 Monthly Heartfelt Prompting Cards (stamps included)',
      description:
        'Send 20 Heartfelt Prompting Cards a month, stamps included. We stuff, stamp, and mail them for you immediately!',
      isIncluded: true,
    },
    {
      title: '20% Discount on Gifts',
      description:
        'Each gift that you add to greeting cards gets you even more savings!',
      isIncluded: true,
    },
    {
      title: '$1.75 Additional Cards',
      description:
        'Each additional card or System Card, pay just $1.75. Stamps not included.',
      isIncluded: true,
    },
    {
      title: 'Contact Manager',
      description:
        'Store your contacts information - such as their address, birthdays, special occasions, reminders, include notes, and get a history of every card sent.',
      isIncluded: true,
    },
    {
      title: 'Premium Storage',
      description:
        'Premium storage allows you to save unlimited cards, pictures, and contacts.',
      isIncluded: true,
    },
  ],
  '128': [
    {
      title: 'Up to 5 Heartfelt Prompting Cards Daily! (Stamps included)',
      description: 'Send 5 Heartfelt Prompting Cards per day, stamps included.',
      isIncluded: true,
    },
    {
      title: '30% Discount on Gifts',
      description:
        'Each gift that you add to greeting cards gets you even more savings!',
      isIncluded: true,
    },
    {
      title: 'Premier Shipping (20% off standard shipping)',
      description:
        'Get even more savings when you ship your greeting card with a gift. We have you covered!',
      isIncluded: true,
    },
    {
      title: '$1.50 Additional Cards',
      description:
        'Each additional card or System Card, pay just $1.50. Stamps not included.',
      isIncluded: true,
    },
    {
      title: 'Contact Manager',
      description:
        'Store your contacts information - such as their address, birthdays, special occasions, reminders, include notes, and get a history of every card sent!',
      isIncluded: true,
    },
    {
      title: 'Premium Storage',
      description:
        'Premium storage allows you to save unlimited cards, pictures, and contacts.',
      isIncluded: true,
    },
  ],
  '129': [
    {
      title: 'Up to 5 Heartfelt Prompting Cards Daily! (Stamps included)',
      description:
        'Send 1-5 Heartfelt Prompting Cards per day, stamps included.',
      isIncluded: true,
    },
    {
      title: 'Up to 5 Best Seller Gifts Monthly (Shipping included)',
      description: 'Send 1-5 Best Seller Gifts per month, stamps included.',
      isIncluded: true,
    },
    {
      title: '30% Discount on Gifts',
      description:
        'Each gift that you add to greeting cards gets you even more savings!',
      isIncluded: true,
    },
    {
      title: 'Premier Shipping (20% off standard shipping)',
      description:
        'Get even more savings when you ship your greeting card with a gift. We have you covered!',
      isIncluded: true,
    },
    {
      title: '$1.25 Additional Cards',
      description:
        'Each additional card or System Card, pay just $1.25. Stamps not included.',
      isIncluded: true,
    },
    {
      title: 'Contact Manager',
      description:
        'Store your contacts information - such as their address, birthdays, special occasions, reminders, include notes, and get a history of every card sent!',
      isIncluded: true,
    },
    {
      title: 'Premium Storage',
      description:
        'Premium storage allows you to save unlimited cards, pictures, and contacts.',
      isIncluded: true,
    },
  ],
}

type CardUpsaleProps = {
  selectedPlanId?: string
}

const getPlanId = (optionId: string) => {
  if (optionId === '324132') {
    return '6'
  } else if (optionId === '7') {
    return '4'
  } else if (optionId === '8') {
    return '39'
  }
  return optionId
}

const plansDescriptionToPricingTile: (
  plansDescription: PlansDescription,
) => ComparisonItemProps[] | undefined = plansDescription => {
  const description = plansDescription.description
  return description?.details !== undefined
    ? description.details.map(detail => {
        const description =
          detail.innerDetails && detail.innerDetails?.length > 1
            ? detail.innerDetails?.map(innerDetail => innerDetail.detail)
            : detail.innerDetails?.[0].detail
        return {
          title: detail.detail,
          description,
          isIncluded: true,
        }
      })
    : undefined
}

enum UpsaleSteps {
  SelectingPlan,
  PurchasingPlan,
}

export const CardUpsale: FC<CardUpsaleProps> = props => {
  const { selectedPlanId = '' } = props

  const { width } = useSelector(state => state.window)

  const [isOpen, setIsOpen] = useState(true)
  const [optionId, setOptionId] = useState(
    selectedPlanId ? getPlanId(selectedPlanId) : '',
  )
  const [isPurchasingDisabled, setIsPurchasingDisabled] = useState(true)
  const innerPaymentRef = useRef<InnerPaymentSheetHandle>(null)
  const { promotionalAddons } = usePlanAddons()
  const actions = useActions()

  const [step, setStep] = useState<UpsaleSteps>(UpsaleSteps.SelectingPlan)

  const [upsaleResponseData, setUpsaleResponseData] = useState<
    UpsaleResponseData | undefined
  >(undefined)

  const [plansDescriptionFragments] = useQueries(getPlansDescriptions())
  const plansDescriptions = plansDescriptionFragments
    .map(PlansDescription.fromPlansDescriptionFragment)
    .filter(value => !value.description.isPromotional)

  const activeOption = options.find(option => option.optionId === optionId)
  const activeOptionDescription = [
    ...plansDescriptions,
    ...promotionalAddons,
  ].find(d => d.id === activeOption?.optionId)
  const activeComparisonList =
    comparisonItems[optionId] ??
    (activeOptionDescription !== undefined
      ? plansDescriptionToPricingTile(activeOptionDescription)
      : undefined)

  const handleSelectedOption = (optionId: string) => {
    setOptionId(getPlanId(optionId ?? '42'))
  }

  const submitAction = {
    title: 'Purchase',
    type: 'success' as ButtonType,
    isDisabled: isPurchasingDisabled,
    onClick: () => {
      if (innerPaymentRef.current) {
        innerPaymentRef.current.updateAccount()
      }
    },
  }

  const handleSetIsOpen = (newIsOpen: boolean) => {
    setIsOpen(newIsOpen)
    if (!newIsOpen) {
      actions.clearRouteArgs()
      actions.openAccount()
    }
  }

  const goToPricingPage = () => {
    setIsOpen(false)
    actions.clearRouteArgs()
    actions.openPricing()
  }

  const updateUpsaleResponseData = (
    uResponseData: UpsaleResponseData | undefined,
  ) => {
    setUpsaleResponseData(uResponseData)
  }

  return (
    <Div data-upsale="upsale-dialog">
      <UpsaleDialog
        title="Great Choice!"
        description="We are glad you found the plan that works best for you. Please confirm below."
        maxWidth={{ xSmall: '100%', small: 'calc(100% - 150px)' }}
        maxHeight={{ xSmall: '90%', small: 'calc(100% - 150px)' }}
        optionFormLocation="right"
        setIsDialogOpen={handleSetIsOpen}
        isOpen={isOpen}
        selectableOptions={options}
        selectedOption={optionId}
        handleSelectedOption={handleSelectedOption}
        acceptSelectedAction={{
          title: 'Continue to Checkout',
          onClick: () => setStep(UpsaleSteps.PurchasingPlan),
        }}
        submitAction={
          step === UpsaleSteps.PurchasingPlan ? submitAction : undefined
        }
        removeSelectedOption={() => {
          setOptionId('')
          setStep(UpsaleSteps.SelectingPlan)
        }}
        checkoutTitle="Great Choice!"
        checkoutDescription="We are glad you found the plan that works best for you. Please confirm below."
        upsaleContentTitle="Includes"
        upsaleContent={
          <ComparisonIncludesList
            isMinified={width <= 1017}
            items={activeComparisonList ?? []}
          />
        }
        extraLink={{
          title: 'See More Package Options',
          onClick: goToPricingPage,
        }}
        shouldHideSendFreeCard={true}
        successComponent={
          typeof upsaleResponseData !== undefined && upsaleResponseData ? (
            <Flex
              alignItems={'center'}
              justifyContent={'center'}
              width={'100%'}
              flexDirection={'column'}
              height={'100%'}
              data-mktcontent={upsaleResponseData.dataMktContent}
            >
              <Div width={'100%'}>
                <Transition
                  title={upsaleResponseData.title}
                  isLoading={upsaleResponseData.isLoading}
                  subtitle={upsaleResponseData.subTitle}
                  loadedStatus={upsaleResponseData.loadedStatus}
                />
              </Div>
              {upsaleResponseData.loadedStatus !== undefined && (
                <Div outset={{ horizontal: 'auto' }}>
                  <Spacer space="x2" />
                  {upsaleResponseData.loadedStatus === 'success' && (
                    <Button
                      title={'Continue to account'}
                      onClick={() => {
                        handleSetIsOpen(false)
                      }}
                    />
                  )}
                  {upsaleResponseData.loadedStatus === 'error' && (
                    <Button
                      fullWidth={true}
                      title={'Go back'}
                      onClick={() => setUpsaleResponseData(undefined)}
                    />
                  )}
                </Div>
              )}
            </Flex>
          ) : undefined
        }
      >
        {optionId === '122' ? (
          <PlanUpsaleInnerPaymentSheet
            planId={getPlanId(optionId)}
            updateUpsaleResponseData={updateUpsaleResponseData}
            isAddon={true}
          />
        ) : (
          <InnerPaymentSheet
            planId={getPlanId(optionId)}
            updateUpsaleResponseData={updateUpsaleResponseData}
            handleIsPurchasingDisabled={setIsPurchasingDisabled}
            ref={innerPaymentRef}
          />
        )}
      </UpsaleDialog>
    </Div>
  )
}

export default CardUpsale
